import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Building2 } from "lucide-react";

const canLocate = typeof navigator !== "undefined" && !!navigator.geolocation;
const getPosition = () => new Promise((resolve, reject) =>
  navigator.geolocation.getCurrentPosition((p) => resolve(p.coords), reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }));

export function QuickAddProspect({ date, repId, onAdded, profileBase = "/crm/customers", className = "mt-6" }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [logVisit, setLogVisit] = useState(true);
  const [useLoc, setUseLoc] = useState(canLocate);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const company = name.trim();
    if (!company) { toast.error("Enter a company name"); return; }
    setBusy(true);
    const posPromise = useLoc ? getPosition().catch(() => null) : Promise.resolve(null);
    try {
      const payload = { company_name: company };
      if (repId && repId !== "all") payload.assigned_rep_id = repId;
      const { data } = await api.post("/crm/customers", payload);
      const id = data.customer.id;
      if (logVisit) {
        await api.post(`/crm/customers/${id}/calls`, { event_type: "In Person Visit", call_date: date, notes: "Added from My Day" });
      }
      const coords = await posPromise;
      let located = false;
      if (coords) {
        located = await api.post(`/crm/customers/${id}/locate`, { lat: coords.latitude, lng: coords.longitude }).then(() => true).catch(() => false);
      }
      setName("");
      const bits = [logVisit && "visit logged", located && "location saved"].filter(Boolean).join(", ");
      toast.success(`${company} added as a prospect${bits ? ` (${bits})` : ""}`, {
        action: { label: "Open", onClick: () => navigate(`${profileBase}/${id}`) },
      });
      if (useLoc && !located) toast.warning("Couldn't get your location — allow location access to pin new prospects on the map");
      onAdded();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add prospect");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className={`${className} bg-white border border-zinc-200 p-3 flex flex-col sm:flex-row sm:items-center gap-3`} data-testid="quick-add-prospect">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Building2 className="h-4 w-4 text-[#2563EB] shrink-0" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Quick add prospect — company name"
          data-testid="quick-add-prospect-input" className="flex-1 min-w-0 px-3 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
      </div>
      <label className="flex items-center gap-2 text-xs text-zinc-600 whitespace-nowrap">
        <input type="checkbox" checked={logVisit} onChange={(e) => setLogVisit(e.target.checked)} data-testid="quick-add-prospect-log-visit" className="accent-[#2563EB]" />
        Log an in-person visit for this day
      </label>
      {canLocate && (
        <label className="flex items-center gap-2 text-xs text-zinc-600 whitespace-nowrap">
          <input type="checkbox" checked={useLoc} onChange={(e) => setUseLoc(e.target.checked)} data-testid="quick-add-prospect-use-location" className="accent-[#2563EB]" />
          Pin at my current location
        </label>
      )}
      <button type="submit" disabled={busy} data-testid="quick-add-prospect-submit"
        className="flex items-center justify-center gap-2 bg-[#2563EB] text-white px-4 py-2 text-sm font-medium hover:bg-[#1D4FD7] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add prospect
      </button>
    </form>
  );
}
